/**
 * Orders FSM (Finite State Machine)
 *
 * FSM = единственный источник бизнес-логики для UI.
 * Все функции — чистые, не вызывают API, не мутируют данные.
 *
 * СТРУКТУРА ДАННЫХ (по контракту OfferWithModelResponse):
 * - order.offer.status → pending | approved | declined
 * - order.deal.status → active | canceled | finished
 * - order.states[] → массив DealState (НА ВЕРХНЕМ УРОВНЕ!)
 * - order.cancel → { actor, reason } (НА ВЕРХНЕМ УРОВНЕ!)
 *
 * ВАЖНО: FSM работает ТОЛЬКО от order.states[], НЕ от order.deal.states[]
 */

import {
  OfferStatus,
  DealStatus,
  DealStateType,
  DealStateStatus,
  UserType,
  OrderUIState,
  OrderEvent,
  EVENT_ORDER,
  CancelActor,
  OrderSystemReason,
} from "./order.types.js"

// ============================================================================
// HELPER: GET LATEST STATE BY TYPE
// ============================================================================

/**
 * Получает последнее состояние указанного типа из states[]
 *
 * @param {import('./order.types.js').DealState[]} states - Массив состояний
 * @param {string} type - Тип состояния (DealStateType)
 * @returns {import('./order.types.js').DealState|null}
 */
function getLatestState(states, type) {
  if (!states || !Array.isArray(states)) {
    return null
  }

  // Ищем с начала массива — API возвращает states отсортированные по id DESC (новые первые)
  for (let i = 0; i < states.length; i++) {
    if (states[i].type === type) {
      return states[i]
    }
  }

  return null
}

/**
 * Получает текущий активный этап сделки
 * Возвращает последнее состояние с pending статусом
 *
 * @param {import('./order.types.js').DealState[]} states - Массив состояний
 * @returns {import('./order.types.js').DealState|null}
 */
function getCurrentActiveState(states) {
  if (!states || !Array.isArray(states)) {
    return null
  }

  // Ищем с начала — API возвращает states отсортированные по id DESC (новые первые)
  // Игнорируем offer states — они не относятся к этапам сделки
  // Проверяем ended_at === null чтобы найти ТЕКУЩЕЕ активное состояние
  for (let i = 0; i < states.length; i++) {
    const state = states[i]
    if (state.type === DealStateType.OFFER) continue
    if (state.status === DealStateStatus.PENDING && !state.ended_at) {
      return state
    }
  }

  return null
}

/**
 * Есть ли в states запись type: offer, status: expired (заявка истекла по таймауту)
 * @param {import('./order.types.js').DealState[]} states
 * @returns {boolean}
 */
function hasOfferExpiredInStates(states) {
  if (!states || !Array.isArray(states)) return false
  return states.some(
    (s) =>
      s.type === DealStateType.OFFER && s.status === DealStateStatus.EXPIRED
  )
}

/**
 * Проверяет, есть ли ожидающий запрос на продление доставки.
 * Запрос на продление определяется так:
 * - Есть delivery state с status="pending" ИЛИ "delayed" (текущий)
 * - Есть хотя бы один предыдущий delivery state с ended_at (был завершён/заменён)
 *
 * @param {import('./order.types.js').DealState[]} states - Массив состояний
 * @returns {boolean}
 */
function isDeliveryExtensionPending(states) {
  if (!states || !Array.isArray(states)) {
    return false
  }

  // Собираем все delivery states
  const deliveryStates = states.filter((s) => s.type === DealStateType.DELIVERY)

  // action_required с бэка = запрос на продление, ожидается ответ реселлера (Принять/Отклонить)
  const hasActionRequired = deliveryStates.some(
    (s) => s.status === DealStateStatus.ACTION_REQUIRED && !s.ended_at
  )
  if (hasActionRequired) {
    return true
  }

  // status "delayed" без action_required = заказ задерживается (одна кнопка «Отменить»), не запрос на продление
  return false
}

/**
 * Есть ли в states активный delivery со status delayed (заказ задерживается)
 * @param {import('./order.types.js').DealState[]} states
 * @returns {boolean}
 */
function hasDeliveryDelayedInStates(states) {
  if (!states || !Array.isArray(states)) return false
  return states.some(
    (s) =>
      s.type === DealStateType.DELIVERY &&
      s.status === DealStateStatus.DELAYED &&
      !s.ended_at
  )
}

/**
 * Есть ли в states активный pickup со status delayed (время получения увеличено)
 * @param {import('./order.types.js').DealState[]} states
 * @returns {boolean}
 */
function hasPickupDelayedInStates(states) {
  if (!states || !Array.isArray(states)) return false
  return states.some(
    (s) =>
      s.type === DealStateType.PICKUP &&
      s.status === DealStateStatus.DELAYED &&
      !s.ended_at
  )
}

/**
 * Есть ли в states payment со status failed или cancelled (заказ отменён автоматически)
 * @param {import('./order.types.js').DealState[]} states
 * @returns {boolean}
 */
function hasPaymentFailedOrCancelledInStates(states) {
  if (!states || !Array.isArray(states)) return false
  return states.some(
    (s) =>
      s.type === DealStateType.PAYMENT &&
      (s.status === DealStateStatus.FAILED ||
        s.status === DealStateStatus.CANCELLED)
  )
}

/**
 * Есть ли в states запись auth с status failed (товар не прошёл аутентификацию)
 *
 * @param {import('./order.types.js').DealState[]} states
 * @returns {boolean}
 */
function hasAuthFailedInStates(states) {
  if (!states || !Array.isArray(states)) return false
  return states.some(
    (s) => s.type === DealStateType.AUTH && s.status === DealStateStatus.FAILED
  )
}

/**
 * Получает новый запрошенный дедлайн из активного DELAYED delivery state
 *
 * @param {import('./order.types.js').DealState[]} states - Массив состояний
 * @returns {string|null} - expires_at активного DELAYED delivery или null
 */
function getExtensionRequestedDeadline(states) {
  if (!states || !Array.isArray(states)) {
    return null
  }

  // Ищем активный DELAYED или ACTION_REQUIRED delivery state (ожидает ответа на продление)
  for (const state of states) {
    if (state.type !== DealStateType.DELIVERY || state.ended_at) continue
    if (
      state.status === DealStateStatus.DELAYED ||
      state.status === DealStateStatus.ACTION_REQUIRED
    ) {
      return state.expires_at || null
    }
  }

  return null
}

// ============================================================================
// DERIVE UI STATE
// ============================================================================

/**
 * Вычисляет UI состояние заказа на основе backend-данных.
 *
 * Приоритет:
 * 1. offer.status = declined → REQUEST_REJECTED
 * 2. deal.status = canceled → CANCELLED_BY_* (читаем order.cancel)
 * 3. deal.status = finished → COMPLETED
 * 4. offer.status = pending → PENDING_REQUEST
 * 5. offer.status = approved без deal → WAITING_PAYMENT
 * 6. Определяем по текущему активному state в order.states[]
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order - Данные заказа
 * @param {string} [userType] - Тип пользователя
 * @returns {string|null} UI состояние из OrderUIState или null
 */
export function deriveOrderUIState(order, userType) {
  // null — это "данные не пришли", UI обрабатывает как loading/error
  if (!order || !order.offer) {
    return null
  }

  const { offer, deal, states, cancel } = order

  // ========== 0. Есть ли в states тип offer + status expired (системная отмена по таймауту заявки) ==========
  // Проверяем до "deal отменён", чтобы при ответе seller/deal с states всегда срабатывал CANCELLED_BY_SYSTEM
  if (hasOfferExpiredInStates(states)) {
    return OrderUIState.CANCELLED_BY_SYSTEM
  }

  // ========== 1. Offer отклонён ==========
  if (offer.status === OfferStatus.DECLINED) {
    // Проверяем, был ли это торг (seller_price существует)
    const hadBidFromSeller = offer.items?.some(
      (item) => item.seller_price != null && Number(item.seller_price) > 0
    )

    // Если был торг (seller сделал встречное предложение) и offer declined:
    // - Это реселлер отклонил торг (BID_REJECTED)
    // - Потому что селлер не стал бы отклонять свой собственный counter-offer
    if (hadBidFromSeller) {
      return OrderUIState.BID_REJECTED
    }

    // Иначе это селлер отклонил оригинальную заявку
    return OrderUIState.REQUEST_REJECTED
  }

  // ========== 2. Deal отменён ==========
  // Проверяем статус deal ИЛИ наличие cancel объекта (для отмены до/во время offer stage)
  if (deal?.status === DealStatus.CANCELED || cancel?.actor) {
    return deriveCancelledState(cancel)
  }

  // ========== 3. Deal завершён ==========
  if (deal?.status === DealStatus.FINISHED) {
    // Проверяем, есть ли активный refund
    const refundState = getLatestState(states, DealStateType.REFUND)
    if (refundState && refundState.status === DealStateStatus.PENDING) {
      return OrderUIState.RETURN_REQUESTED
    }

    // Проверяем, нужно ли seller привязать карту для получения оплаты
    if (
      userType === UserType.SELLER &&
      order.seller_payment_required === true
    ) {
      return OrderUIState.PAYMENT_REQUIRED
    }

    return OrderUIState.COMPLETED
  }

  // ========== 4. Offer pending (ждём решения seller'а) ==========
  if (offer.status === OfferStatus.PENDING) {
    // Проверяем, есть ли встречное предложение от селлера (торги)
    // seller_price в items означает, что селлер сделал встречное предложение
    const hasBidFromSeller = offer.items?.some(
      (item) => item.seller_price != null && Number(item.seller_price) > 0
    )

    if (hasBidFromSeller) {
      // Используем флаг is_trading_complete для определения завершения торгов
      // is_trading_complete: false — торги ещё идут
      // is_trading_complete: true — торги завершены
      // Флаг может быть в deal (OfferWithModelResponse) или на верхнем уровне (DealCompositeResponse)
      const isTradingComplete =
        order.deal?.is_trading_complete === true ||
        order.is_trading_complete === true

      if (!isTradingComplete) {
        // Торги ещё активны
        return OrderUIState.BID_PENDING
      }
      // Торги завершились — ждём финального подтверждения от селлера
    }

    return OrderUIState.PENDING_REQUEST
  }

  // ========== 5. Прочие expired (payment, delivery и т.д.) — системная отмена ==========
  if (states && Array.isArray(states)) {
    const hasExpiredState = states.some(
      (s) => s.status === DealStateStatus.EXPIRED
    )
    if (hasExpiredState) {
      return OrderUIState.CANCELLED_BY_SYSTEM
    }
  }

  // ========== 5b. payment.failed / payment.cancelled — заказ отменён автоматически ==========
  if (hasPaymentFailedOrCancelledInStates(states)) {
    return OrderUIState.CANCELLED_BY_SYSTEM
  }

  // ========== 5b1. finished.success в states — сделка завершена (приоритет над refund.storage/auth.storage) ==========
  if (deal?.status === DealStatus.ACTIVE && states && Array.isArray(states)) {
    const finishedState = getLatestState(states, DealStateType.FINISHED)
    if (finishedState && finishedState.status === DealStateStatus.SUCCESS) {
      return OrderUIState.COMPLETED
    }
  }

  // ========== 5b2. refund.storage — платное хранение при возврате (приоритет над auth.failed: следующий этап после отмены по аутентификации) ==========
  if (deal?.status === DealStatus.ACTIVE && states && Array.isArray(states)) {
    const refundStorageState = states.find(
      (s) =>
        s.type === DealStateType.REFUND &&
        s.status === DealStateStatus.STORAGE &&
        !s.ended_at
    )
    if (refundStorageState) {
      return userType === UserType.SELLER
        ? OrderUIState.PAID_STORAGE
        : OrderUIState.CANCELLED_BY_SYSTEM
    }
  }

  // ========== 5c. auth.storage — платное хранение после auth.failed (селлер — платное хранение, реселлер — сделка отменена, не прошла аутентификация) (тот же экран для селлера, что при refund.storage) ==========
  if (deal?.status === DealStatus.ACTIVE && states && Array.isArray(states)) {
    const authStorageState = states.find(
      (s) =>
        s.type === DealStateType.AUTH &&
        s.status === DealStateStatus.STORAGE &&
        !s.ended_at
    )
    if (authStorageState) {
      return userType === UserType.SELLER
        ? OrderUIState.PAID_STORAGE
        : OrderUIState.CANCELLED_BY_SYSTEM
    }
  }

  // ========== 5d. auth.failed — товар не прошёл аутентификацию ==========
  if (hasAuthFailedInStates(states)) {
    return OrderUIState.CANCELLED_BY_SYSTEM
  }

  // ========== 6. Offer approved, но deal не создан (ждём оплаты) ==========
  if (offer.status === OfferStatus.APPROVED && !deal) {
    return OrderUIState.WAITING_PAYMENT
  }

  // ========== 7. Deal активен — определяем по states[] ==========
  if (deal?.status === DealStatus.ACTIVE) {
    return deriveActiveState(order, userType)
  }

  // Fallback
  return null
}

/**
 * Определяет UI состояние для отменённой сделки
 *
 * @param {import('./order.types.js').DealCancel|null} cancel
 * @returns {string}
 */
function deriveCancelledState(cancel) {
  if (!cancel || !cancel.actor) {
    return OrderUIState.CANCELLED
  }

  switch (cancel.actor) {
    case CancelActor.SELLER:
      return OrderUIState.CANCELLED_BY_SELLER

    case CancelActor.RESELLER:
      return OrderUIState.CANCELLED_BY_RESELLER

    case CancelActor.SYSTEM:
      return OrderUIState.CANCELLED_BY_SYSTEM

    default:
      return OrderUIState.CANCELLED
  }
}

/**
 * Определяет UI состояние для активной сделки по order.states[]
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order
 * @param {string} [userType]
 * @returns {string}
 */
function deriveActiveState(order, userType) {
  const { states, deal } = order

  // finished.success — сделка полностью завершена (приоритет над auth.storage/refund.storage)
  const finishedState = getLatestState(states, DealStateType.FINISHED)
  if (finishedState && finishedState.status === DealStateStatus.SUCCESS) {
    return OrderUIState.COMPLETED
  }

  // Проверяем, есть ли pending запрос на продление доставки (action_required)
  if (isDeliveryExtensionPending(states)) {
    return OrderUIState.EXTEND_REQUESTED
  }

  // Последний delivery (states по id DESC) — failed: автоотмена по дедлайну, не SELLER_WORKING из-за старого pending
  const latestDeliveryFailed = getLatestState(states, DealStateType.DELIVERY)
  if (
    deal?.status === DealStatus.ACTIVE &&
    latestDeliveryFailed?.status === DealStateStatus.FAILED
  ) {
    return OrderUIState.DELIVERY_TIMEOUT_AUTO_CLOSED
  }

  // delivery.delayed с бэка (без action_required) — заказ задерживается, одна кнопка «Отменить»
  if (hasDeliveryDelayedInStates(states)) {
    return OrderUIState.DELIVERY_DELAYED
  }

  // pickup.delayed — время получения увеличено (остальное как при pickup.pending)
  if (hasPickupDelayedInStates(states)) {
    return OrderUIState.PICKUP_DELAYED
  }

  // refund.storage = платное хранение при возврате: селлер — «Платное хранение», реселлер — как при Refund Pending
  const refundStorageState = states?.find(
    (s) =>
      s.type === DealStateType.REFUND &&
      s.status === DealStateStatus.STORAGE &&
      !s.ended_at
  )
  if (refundStorageState) {
    return userType === UserType.SELLER
      ? OrderUIState.PAID_STORAGE
      : OrderUIState.CANCELLED_BY_SYSTEM
  }

  const authStorageState = states?.find(
    (s) =>
      s.type === DealStateType.AUTH &&
      s.status === DealStateStatus.STORAGE &&
      !s.ended_at
  )
  if (authStorageState) {
    return userType === UserType.SELLER
      ? OrderUIState.PAID_STORAGE
      : OrderUIState.CANCELLED_BY_SYSTEM
  }

  // refund.success = товар возвращён селлеру: селлер — «Товар возвращен селлеру», реселлер — как RETURN_REQUESTED
  const refundState = getLatestState(states, DealStateType.REFUND)
  if (refundState && refundState.status === DealStateStatus.SUCCESS) {
    return userType === UserType.SELLER
      ? OrderUIState.REFUND_RETURNED_TO_SELLER
      : OrderUIState.RETURN_REQUESTED
  }

  // pickup.storage = платное хранение на этапе выдачи: селлер — «Сделка закрыта», реселлер — как у селлера при refund storage
  const pickupStorageState = states?.find(
    (s) =>
      s.type === DealStateType.PICKUP &&
      s.status === DealStateStatus.STORAGE &&
      !s.ended_at
  )
  if (pickupStorageState) {
    return OrderUIState.PICKUP_STORAGE
  }

  // Получаем текущее активное состояние
  const currentState = getCurrentActiveState(states)

  if (!currentState) {
    // Нет активного состояния — проверяем по типам
    const paymentState = getLatestState(states, DealStateType.PAYMENT)

    // Если оплата не прошла — ждём оплату
    if (!paymentState || paymentState.status !== DealStateStatus.SUCCESS) {
      return OrderUIState.WAITING_PAYMENT
    }

    // Оплата прошла, проверяем delivery
    const deliveryState = getLatestState(states, DealStateType.DELIVERY)
    if (!deliveryState) {
      return OrderUIState.SELLER_WORKING
    }

    // Delivery success — проверяем auth
    if (deliveryState.status === DealStateStatus.SUCCESS) {
      const authState = getLatestState(states, DealStateType.AUTH)
      if (!authState) {
        return OrderUIState.SELLER_WORKING
      }
      if (authState.status === DealStateStatus.SUCCESS) {
        const pickupState = getLatestState(states, DealStateType.PICKUP)
        if (!pickupState || pickupState.status === DealStateStatus.PENDING) {
          return OrderUIState.READY_FOR_PICKUP
        }
        if (pickupState.status === DealStateStatus.SUCCESS) {
          // Проверяем finished.success — сделка полностью завершена
          const finishedState = getLatestState(states, DealStateType.FINISHED)
          if (
            finishedState &&
            finishedState.status === DealStateStatus.SUCCESS
          ) {
            return OrderUIState.COMPLETED
          }
          return OrderUIState.PICKED_UP_BY_RESELLER
        }
      }
    }

    // Fallback
    return OrderUIState.SELLER_WORKING
  }

  // Маппинг текущего state → UI state
  switch (currentState.type) {
    case DealStateType.PAYMENT:
      if (currentState.status === DealStateStatus.PENDING) {
        return OrderUIState.WAITING_PAYMENT
      }
      break

    case DealStateType.DELIVERY:
      if (currentState.status === DealStateStatus.PENDING) {
        return OrderUIState.SELLER_WORKING
      }
      // delivery.delayed — задержка доставки
      if (currentState.status === DealStateStatus.DELAYED) {
        return OrderUIState.DELIVERY_DELAYED
      }
      // delivery.success — товар доставлен в пункт, начинается аутентификация
      if (currentState.status === DealStateStatus.SUCCESS) {
        return OrderUIState.IN_AUTHENTICATION
      }
      break

    case DealStateType.AUTH:
      if (currentState.status === DealStateStatus.PENDING) {
        return OrderUIState.IN_AUTHENTICATION
      }
      if (currentState.status === DealStateStatus.SUCCESS) {
        return OrderUIState.READY_FOR_PICKUP
      }
      // auth.technical_failed — техническая ошибка
      if (currentState.status === DealStateStatus.TECHNICAL_FAILED) {
        return OrderUIState.TECHNICAL_ERROR
      }
      break

    case DealStateType.PICKUP:
      if (currentState.status === DealStateStatus.PENDING) {
        return OrderUIState.READY_FOR_PICKUP
      }
      // pickup.delayed — время получения увеличено
      if (currentState.status === DealStateStatus.DELAYED) {
        return OrderUIState.PICKUP_DELAYED
      }
      // pickup.storage — селлер: «Сделка закрыта», реселлер: как refund storage
      if (currentState.status === DealStateStatus.STORAGE) {
        return OrderUIState.PICKUP_STORAGE
      }
      // Pickup success означает товар получен реселлером
      if (currentState.status === DealStateStatus.SUCCESS) {
        return OrderUIState.PICKED_UP_BY_RESELLER
      }
      break

    case DealStateType.FINISHED:
      // finished.pending = заказ получен реселлером, 48ч на возврат
      if (currentState.status === DealStateStatus.PENDING) {
        return OrderUIState.PICKED_UP_BY_RESELLER
      }
      // finished.success = сделка полностью завершена
      return OrderUIState.COMPLETED

    case DealStateType.REFUND:
      if (currentState.status === DealStateStatus.PENDING) {
        return OrderUIState.RETURN_REQUESTED
      }
      if (currentState.status === DealStateStatus.REFUNDED) {
        return OrderUIState.REFUND_COMPLETED
      }
      break

    case DealStateType.REFUND_DELIVERY:
      // Возврат в процессе доставки
      return OrderUIState.REFUND_IN_DELIVERY

    case DealStateType.REFUND_FINISHED:
      // Возврат завершён
      return OrderUIState.REFUND_COMPLETED

    default:
      break
  }

  // Fallback: если не определили — показываем "Заказ оплачен"
  return OrderUIState.SELLER_WORKING
}

// ============================================================================
// CAN TRIGGER EVENT
// ============================================================================

/**
 * Проверяет, может ли пользователь выполнить указанное событие.
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order - Данные заказа
 * @param {string} event - Событие из OrderEvent
 * @param {string} userType - Тип пользователя (seller/reseller)
 * @returns {boolean}
 */
export function canTriggerEvent(order, event, userType) {
  if (!order || !order.offer) {
    return false
  }

  const { offer, deal, states } = order
  const isSeller = userType === UserType.SELLER
  const isReseller = userType === UserType.RESELLER
  const uiState = deriveOrderUIState(order, userType)

  switch (event) {
    // ========== OFFER EVENTS ==========
    // Эти события доступны ТОЛЬКО когда сделки ещё нет

    case OrderEvent.ACCEPT_REQUEST:
      // Принять оффер — только seller и offer pending; не показывать при истёкшей заявке (offer/expired)
      if (!isSeller) return false
      if (hasOfferExpiredInStates(states)) return false
      if (offer.status !== OfferStatus.PENDING) return false
      // Проверяем is_trading_complete - если true, торги завершены, селлер может принять
      const isTradingCompleteForAccept =
        order.deal?.is_trading_complete === true ||
        order.is_trading_complete === true
      if (isTradingCompleteForAccept) return true
      // Если торги не завершены и seller_price есть (реальное значение > 0) - блокируем (ждём решения реселлера)
      const hasSellerPriceForAccept = offer.items?.some(
        (item) => item.seller_price != null && Number(item.seller_price) > 0
      )
      if (hasSellerPriceForAccept) return false
      return true

    case OrderEvent.REJECT_REQUEST:
      // Отклонить оффер — только seller и offer pending; не показывать при истёкшей заявке (offer/expired)
      if (!isSeller) return false
      if (hasOfferExpiredInStates(states)) return false
      if (offer.status !== OfferStatus.PENDING) return false
      // Проверяем is_trading_complete - если true, торги завершены, селлер может отклонить
      const isTradingCompleteForReject =
        order.deal?.is_trading_complete === true ||
        order.is_trading_complete === true
      if (isTradingCompleteForReject) return true
      // Если торги не завершены и seller_price есть (реальное значение > 0) - блокируем (ждём решения реселлера)
      const hasSellerPriceForReject = offer.items?.some(
        (item) => item.seller_price != null && Number(item.seller_price) > 0
      )
      if (hasSellerPriceForReject) return false
      return true

    case OrderEvent.CANCEL_REQUEST:
      // Реселлер НЕ может отменять заявку/оффер — кнопка убрана полностью
      return false

    // ========== PAYMENT EVENTS ==========

    case OrderEvent.PAY:
      if (!isReseller) return false
      // Можно оплатить если offer approved
      if (offer.status !== OfferStatus.APPROVED) return false
      // Если deal отменён — нельзя оплатить
      if (deal?.status === DealStatus.CANCELED) return false
      // Если deal ещё не создан — можно оплатить
      if (!deal) return true
      // Если deal есть — проверяем payment state
      const paymentStateForPay = getLatestState(states, DealStateType.PAYMENT)
      return paymentStateForPay?.status === DealStateStatus.PENDING

    // ========== DEAL EVENTS ==========

    case OrderEvent.UPDATE_STATE:
      if (!isSeller || !deal) return false
      // Seller может обновить состояние при pending delivery
      // Но не во время ожидания подтверждения продления
      const deliveryState = getLatestState(states, DealStateType.DELIVERY)
      return (
        deal.status === DealStatus.ACTIVE &&
        deliveryState?.status === DealStateStatus.PENDING &&
        !isDeliveryExtensionPending(states)
      )

    case OrderEvent.CONFIRM_COMPLETE:
      // Кнопка убрана — реселлер не подтверждает получение
      return false

    case OrderEvent.CANCEL_DEAL:
      // Для CANCEL_DEAL нужна активная сделка
      if (!deal || deal.status !== DealStatus.ACTIVE) return false
      // Нельзя отменить во время ожидания подтверждения продления (action_required)
      if (isDeliveryExtensionPending(states)) return false

      // Реселлер: delivery.failed / автоотмена по доставке — без «Отменить»
      if (isReseller && uiState === OrderUIState.DELIVERY_TIMEOUT_AUTO_CLOSED) {
        return false
      }

      // Проверяем что offer уже approved (сделка создана после оплаты)
      if (offer.status !== OfferStatus.APPROVED) return false

      // delivery.status === "delayed" — одна кнопка «Отменить» у селлера и реселлера
      const deliveryStateForCancel = getLatestState(
        states,
        DealStateType.DELIVERY
      )
      if (
        deliveryStateForCancel?.status === DealStateStatus.DELAYED &&
        !deliveryStateForCancel.ended_at
      ) {
        return true
      }

      if (isReseller) {
        // Reseller может отменить сделку:
        // 1. На этапе ожидания оплаты (payment.pending)
        // 2. В течение 15 минут после оплаты
        // 3. До передачи на склад (пока delivery.pending — аналогично селлеру)
        // НО: после начала аутентификации или на этапе pickup — отмена запрещена

        // Блокируем отмену на этапе auth (товар на проверке / на складе)
        const authState = getLatestState(states, DealStateType.AUTH)
        if (authState) {
          return false
        }

        // Блокируем отмену на этапе pickup (готов к выдаче)
        const pickupState = getLatestState(states, DealStateType.PICKUP)
        if (pickupState) {
          return false
        }

        // Блокируем отмену на этапе finished (заказ получен)
        const finishedState = getLatestState(states, DealStateType.FINISHED)
        if (finishedState) {
          return false
        }

        const paymentState = getLatestState(states, DealStateType.PAYMENT)

        // Разрешаем отмену на этапе ожидания оплаты
        if (paymentState?.status === DealStateStatus.PENDING) {
          return true
        }

        // Разрешаем отмену в течение 15 минут после оплаты
        if (
          paymentState?.status === DealStateStatus.SUCCESS &&
          paymentState?.created_at
        ) {
          const paymentTime = new Date(paymentState.created_at).getTime()
          const now = Date.now()
          const fifteenMinutesMs = 15 * 60 * 1000
          if (now - paymentTime < fifteenMinutesMs) {
            return true
          }
        }

        // Разрешаем отмену до передачи на склад (пока селлер не доставил — delivery.pending, как у селлера)
        const deliveryState = getLatestState(states, DealStateType.DELIVERY)
        if (deliveryState?.status === DealStateStatus.PENDING) {
          return true
        }

        return false
      }

      if (isSeller) {
        // Seller может отменить заказ:
        // 1. На этапе ожидания оплаты (WAITING_PAYMENT)
        // 2. До начала аутентификации (на этапе доставки SELLER_WORKING)
        // После получения реселлером (PICKED_UP_BY_RESELLER) — отмена невозможна

        // Блокируем отмену после получения реселлером
        if (uiState === OrderUIState.PICKED_UP_BY_RESELLER) {
          return false
        }

        const authState = getLatestState(states, DealStateType.AUTH)

        // Блокируем после начала аутентификации
        if (authState) {
          return false
        }

        // Разрешаем на этапе ожидания оплаты (WAITING_PAYMENT)
        const paymentState = getLatestState(states, DealStateType.PAYMENT)
        if (paymentState?.status === DealStateStatus.PENDING) {
          return true
        }

        // Разрешаем на этапе доставки (SELLER_WORKING)
        const deliveryState = getLatestState(states, DealStateType.DELIVERY)
        if (deliveryState?.status === DealStateStatus.PENDING) {
          return true
        }

        return false
      }

      return false

    case OrderEvent.EXTEND_DEADLINE:
      if (!isSeller || !deal) return false
      // Seller может запросить продление если нет pending запроса на продление
      const deliveryForExtend = getLatestState(states, DealStateType.DELIVERY)
      return (
        deal.status === DealStatus.ACTIVE &&
        deliveryForExtend?.status === DealStateStatus.PENDING &&
        !isDeliveryExtensionPending(states)
      )

    case OrderEvent.ACCEPT_EXTEND:
    case OrderEvent.REJECT_EXTEND:
      if (!isReseller || !deal) return false
      // Reseller может принять/отклонить продление если есть pending запрос
      return (
        deal.status === DealStatus.ACTIVE && isDeliveryExtensionPending(states)
      )

    case OrderEvent.REPORT_PROBLEM:
      // После успешного завершения сделки (COMPLETED) или когда товар возвращён селлеру (refund.success)
      return (
        (deal && deal.status === DealStatus.FINISHED) ||
        uiState === OrderUIState.COMPLETED ||
        uiState === OrderUIState.REFUND_RETURNED_TO_SELLER
      )

    case OrderEvent.LINK_CARD:
      // Seller может привязать карту когда сделка завершена и требуется оплата
      return (
        isSeller &&
        deal?.status === DealStatus.FINISHED &&
        order.seller_payment_required === true
      )

    case OrderEvent.CONFIRM_SELLER_READY:
      // Кнопка убрана — после оплаты сразу показывается "Заказ оплачен"
      return false

    case OrderEvent.LEAVE_FEEDBACK:
      return false

    case OrderEvent.RETURN_PRODUCT:
      // Кнопка возврата товара убрана
      return false

    // ========== BIDDING EVENTS (торги) ==========

    case OrderEvent.PROPOSE_PRICE:
      // Seller может сделать встречное предложение если:
      // 1. Он seller
      // 2. Заявка не истекла (offer/expired в states — не показывать кнопку)
      // 3. Offer в статусе pending (PENDING_REQUEST)
      // 4. Реселлер предложил свою цену (есть trade_price)
      // 5. НЕ в BID_PENDING (селлер уже сделал встречное предложение)
      // 6. Торги ещё не завершены (is_trading_complete !== true)
      if (!isSeller) return false
      if (hasOfferExpiredInStates(states)) return false
      if (offer.status !== OfferStatus.PENDING) return false
      if (uiState === OrderUIState.BID_PENDING) return false
      // Флаг может быть в deal или на верхнем уровне
      if (
        order.deal?.is_trading_complete === true ||
        order.is_trading_complete === true
      )
        return false
      // Проверяем что реселлер предложил свою цену
      const hasTradePriceFromReseller = offer.items?.some(
        (item) => item.trade_price !== null && item.trade_price !== undefined
      )
      return hasTradePriceFromReseller

    case OrderEvent.ACCEPT_BID:
    case OrderEvent.REJECT_BID:
      // Reseller может принять/отклонить bid селлера только если:
      // 1. Он reseller
      // 2. Offer в статусе pending
      // 3. Селлер сделал встречное предложение (seller_price есть)
      // 4. Торги ещё не завершены (is_trading_complete !== true)
      if (!isReseller) return false
      if (offer.status !== OfferStatus.PENDING) return false
      // Если торги завершены - реселлер уже принял, ждём решения селлера
      const isTradingCompleteForBid =
        order.deal?.is_trading_complete === true ||
        order.is_trading_complete === true
      if (isTradingCompleteForBid) return false
      // Проверяем, есть ли seller_price (селлер сделал встречное предложение)
      const hasSellerPriceForBid = offer.items?.some(
        (item) => item.seller_price != null && Number(item.seller_price) > 0
      )
      return hasSellerPriceForBid

    default:
      return false
  }
}

// ============================================================================
// GET AVAILABLE EVENTS
// ============================================================================

/**
 * Возвращает список доступных событий для пользователя.
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order
 * @param {string} userType - Тип пользователя (seller/reseller)
 * @returns {string[]} Массив доступных событий из OrderEvent
 */
export function getAvailableEvents(order, userType) {
  if (!order || !order.offer) {
    return []
  }

  return EVENT_ORDER.filter((event) => canTriggerEvent(order, event, userType))
}

// ============================================================================
// HELPER: CHECK IF ORDER IS ACTIVE
// ============================================================================

/**
 * Проверяет, является ли сделка активной.
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order
 * @returns {boolean}
 */
export function isOrderActive(order) {
  if (!order || !order.offer) {
    return false
  }

  return (
    order.offer.status === OfferStatus.APPROVED &&
    order.deal?.status === DealStatus.ACTIVE
  )
}

// ============================================================================
// HELPER: CHECK IF ORDER IS TERMINAL
// ============================================================================

/**
 * Проверяет, находится ли заказ в терминальном состоянии.
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order
 * @returns {boolean}
 */
export function isOrderTerminal(order) {
  const uiState = deriveOrderUIState(order)

  if (uiState === null) {
    return false
  }

  return [
    OrderUIState.COMPLETED,
    OrderUIState.DELIVERY_TIMEOUT_AUTO_CLOSED,
    OrderUIState.CANCELLED,
    OrderUIState.CANCELLED_BY_SELLER,
    OrderUIState.CANCELLED_BY_RESELLER,
    OrderUIState.CANCELLED_BY_SYSTEM,
    OrderUIState.REQUEST_REJECTED,
    OrderUIState.BID_REJECTED,
  ].includes(uiState)
}

// ============================================================================
// HELPER: GET CANCEL INFO
// ============================================================================

/**
 * Получает информацию об отмене сделки
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order
 * @returns {import('./order.types.js').DealCancel|null}
 */
export function getCancelInfo(order) {
  // По контракту cancel на верхнем уровне
  return order?.cancel || null
}

// ============================================================================
// HELPER: GET STATE EXPIRES AT
// ============================================================================

/**
 * Получает время истечения текущего состояния (для таймера)
 * Логика соответствует OrderDetailsPage.dealDeadline()
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order
 * @param {string} [userType] - Тип пользователя (опционально)
 * @returns {string|null}
 */
export function getStateExpiresAt(order, userType) {
  if (!order) return null

  // Получаем UI состояние
  const uiState = deriveOrderUIState(order, userType)

  // RETURN_REQUESTED (refund.pending): таймер только у селлера — expires_at из refund state
  if (uiState === OrderUIState.RETURN_REQUESTED) {
    if (userType === UserType.SELLER) {
      const refundState = getLatestState(
        order.states || [],
        DealStateType.REFUND
      )
      if (refundState?.status === DealStateStatus.PENDING) {
        return refundState.expires_at || null
      }
    }
    return null
  }

  // Не показываем таймер для отменённых и завершённых заказов
  const cancelledOrCompletedStates = [
    OrderUIState.CANCELLED,
    OrderUIState.CANCELLED_BY_SELLER,
    OrderUIState.CANCELLED_BY_RESELLER,
    OrderUIState.CANCELLED_BY_SYSTEM,
    OrderUIState.REQUEST_REJECTED,
    OrderUIState.BID_REJECTED,
    OrderUIState.COMPLETED,
    OrderUIState.DELIVERY_TIMEOUT_AUTO_CLOSED,
    OrderUIState.REFUND_COMPLETED,
    OrderUIState.REFUND_RETURNED_TO_SELLER,
    OrderUIState.PICKED_UP_BY_RESELLER,
  ]
  if (cancelledOrCompletedStates.includes(uiState)) {
    return null
  }

  // EXTEND_REQUESTED: expires_at из delivery action_required
  if (uiState === OrderUIState.EXTEND_REQUESTED) {
    return getExtensionRequestedDeadline(order.states) || null
  }

  const states = order.states || []
  const offerState = getLatestState(states, DealStateType.OFFER)
  const paymentState = getLatestState(states, DealStateType.PAYMENT)
  const deliveryState = getLatestState(states, DealStateType.DELIVERY)
  const authState = getLatestState(states, DealStateType.AUTH)
  const pickupState = getLatestState(states, DealStateType.PICKUP)
  const finishedState = getLatestState(states, DealStateType.FINISHED)

  // Для PENDING_REQUEST — дедлайн принятия заявки из offer state
  if (uiState === OrderUIState.PENDING_REQUEST) {
    return offerState?.expires_at || order.offer?.deadline || null
  }

  // Для BID_PENDING — дедлайн торгов из offer state
  if (uiState === OrderUIState.BID_PENDING) {
    return (
      offerState?.expires_at ||
      order.offer?.bid_deadline ||
      order.offer?.expires_at ||
      order.offer?.deadline ||
      null
    )
  }

  // Для WAITING_PAYMENT — дедлайн оплаты из payment state
  if (uiState === OrderUIState.WAITING_PAYMENT) {
    return (
      paymentState?.expires_at ||
      order.offer?.payment_deadline ||
      order.offer?.expires_at ||
      null
    )
  }

  // Для SELLER_WORKING — дедлайн доставки из delivery state
  if (uiState === OrderUIState.SELLER_WORKING) {
    // Ищем активный (pending) delivery state
    const activeDeliveryState = states.find(
      (s) =>
        s.type === DealStateType.DELIVERY &&
        s.status === DealStateStatus.PENDING
    )
    return activeDeliveryState?.expires_at || deliveryState?.expires_at || null
  }

  // Для IN_AUTHENTICATION — дедлайн из auth state
  if (uiState === OrderUIState.IN_AUTHENTICATION) {
    return authState?.expires_at || null
  }

  // Для READY_FOR_PICKUP и PICKUP_DELAYED — дедлайн из pickup state
  if (
    uiState === OrderUIState.READY_FOR_PICKUP ||
    uiState === OrderUIState.PICKUP_DELAYED
  ) {
    return pickupState?.expires_at || null
  }

  // Для PICKED_UP_BY_RESELLER — дедлайн из finished state (48ч на возврат)
  if (uiState === OrderUIState.PICKED_UP_BY_RESELLER) {
    return finishedState?.expires_at || null
  }

  // PICKUP_STORAGE (pickup.storage): таймер только у реселлера
  if (uiState === OrderUIState.PICKUP_STORAGE) {
    if (
      userType === UserType.RESELLER &&
      pickupState?.status === DealStateStatus.STORAGE
    ) {
      return pickupState.expires_at || null
    }
    return null
  }

  // PAID_STORAGE (refund.storage или auth.storage) — дедлайн из state со статусом storage
  if (uiState === OrderUIState.PAID_STORAGE) {
    const refundState = getLatestState(states, DealStateType.REFUND)
    if (refundState?.status === DealStateStatus.STORAGE) {
      return refundState.expires_at || null
    }
    const authState = getLatestState(states, DealStateType.AUTH)
    if (authState?.status === DealStateStatus.STORAGE) {
      return authState.expires_at || null
    }
    return null
  }

  // Fallback: текущее активное состояние
  const currentState = getCurrentActiveState(states)
  return currentState?.expires_at || null
}

// ============================================================================
// HELPER: DERIVE SYSTEM REASON
// ============================================================================

/**
 * Выводит системную причину отмены из FSM (DealState.type + DealState.status)
 *
 * НЕ используется для пользовательских отмен (cancel.reason).
 * Возвращает null если есть cancel.reason или причина не системная.
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order
 * @returns {string|null} Значение из OrderSystemReason или null
 */
export function deriveSystemReason(order) {
  if (!order) return null

  const { states, cancel } = order

  if (!states || !Array.isArray(states)) return null

  // Если отмена инициирована пользователем (seller/reseller) — это НЕ системная причина
  // Системные причины (PAYMENT_TIMEOUT, REQUEST_TIMEOUT и т.д.) применимы только к system-отменам
  if (
    cancel?.actor === CancelActor.SELLER ||
    cancel?.actor === CancelActor.RESELLER
  )
    return null
  if (cancel?.reason) return null
  if (cancel?.comment) return null

  const deliveryStates = states.filter((s) => s.type === DealStateType.DELIVERY)
  const latestDeliveryForReason = getLatestState(states, DealStateType.DELIVERY)
  // Таймаут доставки: последний delivery — failed (не путать с отклонением продления при нескольких delivery)
  if (latestDeliveryForReason?.status === DealStateStatus.FAILED) {
    return OrderSystemReason.DELIVERY_TIMEOUT
  }
  // Отклонение продления реселлером: последний delivery cancelled после ответа на action_required
  const hadAnsweredExtendRequest = deliveryStates.some(
    (s) => s.status === DealStateStatus.ACTION_REQUIRED && Boolean(s.ended_at)
  )
  if (
    latestDeliveryForReason?.status === DealStateStatus.CANCELLED &&
    hadAnsweredExtendRequest &&
    deliveryStates.length > 1
  ) {
    return OrderSystemReason.EXTENSION_REJECTED
  }

  // Ищем состояние с expired/failed статусом
  for (let i = states.length - 1; i >= 0; i--) {
    const state = states[i]

    // Offer expired → REQUEST_TIMEOUT
    if (
      state.type === DealStateType.OFFER &&
      state.status === DealStateStatus.EXPIRED
    ) {
      return OrderSystemReason.REQUEST_TIMEOUT
    }

    // Payment expired → PAYMENT_TIMEOUT
    if (
      state.type === DealStateType.PAYMENT &&
      state.status === DealStateStatus.EXPIRED
    ) {
      return OrderSystemReason.PAYMENT_TIMEOUT
    }

    // Payment failed → PAYMENT_FAILED
    if (
      state.type === DealStateType.PAYMENT &&
      state.status === DealStateStatus.FAILED
    ) {
      return OrderSystemReason.PAYMENT_FAILED
    }

    // Payment cancelled → PAYMENT_CANCELLED (технические проблемы)
    if (
      state.type === DealStateType.PAYMENT &&
      state.status === DealStateStatus.CANCELLED
    ) {
      return OrderSystemReason.PAYMENT_CANCELLED
    }

    // Delivery expired → DELIVERY_TIMEOUT
    if (
      state.type === DealStateType.DELIVERY &&
      state.status === DealStateStatus.EXPIRED
    ) {
      return OrderSystemReason.DELIVERY_TIMEOUT
    }

    // Auth failed → AUTHENTICATION_FAILED
    if (
      state.type === DealStateType.AUTH &&
      state.status === DealStateStatus.FAILED
    ) {
      return OrderSystemReason.AUTHENTICATION_FAILED
    }

    // Any technical_failed → TECHNICAL_ISSUE
    if (state.status === DealStateStatus.TECHNICAL_FAILED) {
      return OrderSystemReason.TECHNICAL_ISSUE
    }
  }

  return null
}

/**
 * Получает запрошенный дедлайн продления доставки
 * Экспортируется для использования в UI
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order
 * @returns {string|null} - expires_at нового pending delivery или null
 */
export function getExtendRequestedDeadline(order) {
  return getExtensionRequestedDeadline(order?.states)
}

/**
 * Получает ttl_hours из state type=finished, status=pending (срок на возврат товара)
 *
 * @param {import('./order.types.js').OfferWithModelResponse} order
 * @returns {number|null} - ttl_hours или null
 */
export function getFinishedStateTtlHours(order) {
  const states = order?.states
  if (!states || !Array.isArray(states)) return null
  const finishedState = getLatestState(states, DealStateType.FINISHED)
  if (!finishedState || finishedState.status !== DealStateStatus.PENDING)
    return null
  const ttl = finishedState.ttl_hours
  return typeof ttl === "number" && !Number.isNaN(ttl) ? ttl : null
}
